import { Component, computed, inject, signal} from '@angular/core';
import { Router} from '@angular/router';
import { SessionService } from '@/app/services/security/session.service';
import { AuthService } from '@auth0/auth0-angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthenticationService } from '@/app/services/api/authentication.service';
import { ToastrService } from 'ngx-toastr';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FeatureFlagsService } from '@/app/services/feature-flags.service';
import { User } from '../models/users/user';

@Component({
  selector: 'app-auth',
  imports: [CommonModule, FormsModule, ProgressSpinnerModule],
  templateUrl: './auth.html',
  styleUrl: './auth.css',
})
export class Auth {
  public authService = inject(AuthService);
  private authenticationService = inject(AuthenticationService);
  private toastr = inject(ToastrService);
  private sessionService = inject(SessionService);
  private router = inject(Router);
  private featureFlags = inject(FeatureFlagsService);

  ready = signal<boolean>(false);
  loading = signal<boolean>(false);
  showCredentialsLogin = computed(() => this.featureFlags.flags().enableCredentialsLogin);

  email = '';
  password = '';
  showPassword = false;
  private isOktaCallback = false;

  constructor() {
    // Si la URL tiene parámetros code y state, es un callback de Okta.
    // No limpiar localStorage para que Auth0 SDK pueda procesar el callback.
    const params = new URLSearchParams(window.location.search);
    this.isOktaCallback = params.has('code') && params.has('state');

    if (!this.isOktaCallback) {
      localStorage.clear();
    }
  }

  ngOnInit() {
    this.featureFlags.load();

    this.authService.isAuthenticated$.subscribe((isAuth) => {
      if (isAuth && this.isOktaCallback) {
        this.loading.set(true);
      }
      this.ready.set(true);
    });

    this.authService.idTokenClaims$.subscribe((claims: any) => {
      if (!claims) return;
      const idToken = claims.__raw;
      this.loginWithApi(idToken);
    });
  }

  loginWithOkta() {
    this.authService.loginWithRedirect();
  }

  loginWithCredentials() {
    if (!this.email || !this.password) {
      this.toastr.warning('Ingresa correo y contraseña');
      return;
    }

    this.loading.set(true);
    this.authenticationService.login(this.email, this.password).subscribe({
      next: (response) => {
        if (response.success && response.result) {
          const token = response.result.token;
          const claims = JSON.parse(atob(token.split('.')[1]));

          const usuario: User = {
            idUsuario: claims.iu,
            Nombre: claims.n,
            Apellido: claims.a,
            Email: this.email,
            NumeroEmpleado: '',
            idRol: claims.ir,
            Rol: claims.rn,
            Estatus: true,
            ChangePassword: claims.cp ?? false
          };

          this.sessionService.login(token, usuario, 'credentials');
          this.router.navigate(['/home'], { replaceUrl: true });
        } else {
          this.toastr.error(response.message ?? 'Error al iniciar sesión');
        }
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        const detail = err.error?.detail ?? err.error?.message ?? 'Error desconocido, verifica conexión a la red.';
        this.toastr.error(detail);
      }
    });
  }

  private loginWithApi(idToken: string) {
    this.loading.set(true);
    this.authenticationService.exchange_token(idToken).subscribe({
      next: (response) => {
        if (response.success) {
          const token = response.result.token;
          const claims = JSON.parse(atob(token.split('.')[1]));

          const usuario: User = {
            idUsuario: claims.iu,
            Nombre: claims.n,
            Apellido: claims.a,
            Email: '',
            NumeroEmpleado: '',
            idRol: claims.ir,
            Rol: claims.rn,
            Estatus: true,
            ChangePassword: false
          };

          this.sessionService.login(token, usuario, 'okta');
          this.router.navigate(['/home'], { replaceUrl: true });
        } 
        setTimeout(() => this.loading.set(false), 5000);
      },
      error: (err) => {
        // console.error('Token exchange failed', err);
        this.loading.set(false);
        if (err.error) {
          let { detail } = err.error;
          this.toastr.error( detail ?? "Error desconocido, verifica conexión a la red y actualiza la página.");
          setTimeout(() => this.sessionService.logout(), 5000);
        }
      }
    });
  }
}
